import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Sidebar, Heading, Text, Input, Button, LoadingSpinner, SortableTableHeader, Editor, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { modulesService } from '../services/modules'
import { dashcorretorService } from '../services/dashcorretor'
import { serverInfoService } from '../services/serverInfo'
import { userLogsService } from '../services/userLogs'
import { HistoricoAcesso, Broker, DashCorretorParameter, DashCorretorCampeaoMesEntry, DashCorretorDecoleInfluenciadoresEntry, DashCorretorReiImediatosEntry, DashCorretorMinimetas2026Entry } from '../types/dashcorretor'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'

function campaignRowMatchesFilters(
  item: { broker_code?: number | null; broker_name: string; classification?: string | null },
  codeFilter: string,
  nameFilter: string,
  classificationFilter: string
): boolean {
  const code = codeFilter.trim()
  if (code && !String(item.broker_code ?? '').includes(code)) return false
  const name = nameFilter.trim().toLowerCase()
  if (name && !(item.broker_name || '').toLowerCase().includes(name)) return false
  const cls = classificationFilter.trim()
  if (cls && (item.classification || '').trim() !== cls) return false
  return true
}

function formatReferenceMonth(referenceMonth: string): string {
  const [year, month] = referenceMonth.split('-')
  if (year && month && month.length >= 2) {
    return `${month.slice(0, 2)}/${year}`
  }
  return referenceMonth
}

function getReferenceMonthSortValue(referenceMonth: string): number {
  const [year, month] = referenceMonth.split('-')
  const y = Number(year)
  const m = Number(month)
  if (Number.isFinite(y) && Number.isFinite(m)) {
    return y * 100 + m
  }
  return 0
}

const DashboardCorretor: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'historico' | 'corretores' | 'campanhas' | 'parametros'>('campanhas')
  const [activeSubTab, setActiveSubTab] = useState<'minimetas2026' | 'campeaoMes' | 'decoleInfluenciadores' | 'reiImediatos'>('minimetas2026')
  const [historicoCorretorCodeFilter, setHistoricoCorretorCodeFilter] = useState('')
  const [corretorQuery, setCorretorQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [moduleTitle, setModuleTitle] = useState<string>('Dashboard Corretor')
  const [moduleDescription, setModuleDescription] = useState<string>('Visão de acessos por corretor')

  // Controle de visualização de colunas sensíveis (valores monetários).
  // Se a permissão ainda estiver carregando, a opção fica desabilitada (mais seguro).
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    'portais_dashboard_corretor',
    'view_values'
  )

  // Estados para aba de corretores (tabela brokers)
  const [loadingBrokers, setLoadingBrokers] = useState<boolean>(false)
  const [brokers, setBrokers] = useState<Broker[]>([])
  const [corretorCodeFilter, setCorretorCodeFilter] = useState<string>('')
  const [corretorNameFilter, setCorretorNameFilter] = useState<string>('')
  const [corretorClassificationFilter, setCorretorClassificationFilter] = useState<string>('')
  const [corretorUfFilter, setCorretorUfFilter] = useState<string>('')
  const [corretorCityFilter, setCorretorCityFilter] = useState<string>('')
  const [currentPageBrokers, setCurrentPageBrokers] = useState(1)
  const [itemsPerPageBrokers] = useState(20)
  const [acessandoBrokerId, setAcessandoBrokerId] = useState<string | null>(null)

  // Estados para aba de histórico
  const [loadingHistorico, setLoadingHistorico] = useState<boolean>(true)
  const [historicoAcessos, setHistoricoAcessos] = useState<HistoricoAcesso[]>([])
  const [totalHistorico, setTotalHistorico] = useState<number>(0)
  const [acessosHoje, setAcessosHoje] = useState<number>(0)

  // Estados para ordenação
  const [sortBy, setSortBy] = useState<string>('access_timestamp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Navegação horizontal da tabela
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTableBy = (dx: number) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: dx, behavior: 'smooth' })
    }
  }
  const handleTableWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Converte scroll vertical do mouse em horizontal
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollBy({ left: e.deltaY, behavior: 'auto' })
      }
    }
  }

  // Estados para aba de parâmetros
  const [parameters, setParameters] = useState<DashCorretorParameter[]>([])
  const [loadingParameters, setLoadingParameters] = useState<boolean>(false)
  const [editingParameter, setEditingParameter] = useState<DashCorretorParameter | null>(null)
  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false)
  const [parameterEditValue, setParameterEditValue] = useState<string>('')
  const [savingParameter, setSavingParameter] = useState(false)
  const [parameterEditMode, setParameterEditMode] = useState<'simple' | 'html'>('simple')

  // Estados para Campeão do Mês
  const [campeaoMesItems, setCampeaoMesItems] = useState<DashCorretorCampeaoMesEntry[]>([])
  const [loadingCampeaoMes, setLoadingCampeaoMes] = useState<boolean>(false)

  // Estados para "Rei dos Imediatos"
  const [reiImediatosItems, setReiImediatosItems] = useState<DashCorretorReiImediatosEntry[]>([])
  const [loadingReiImediatos, setLoadingReiImediatos] = useState<boolean>(false)
  const [currentPageReiImediatos, setCurrentPageReiImediatos] = useState(1)
  const [itemsPerPageReiImediatos] = useState(20)
  // Filtros compartilhados da aba Campanhas (todas as sub-abas)
  const [campanhasCorretorCodeFilter, setCampanhasCorretorCodeFilter] = useState<string>('')
  const [campanhasCorretorNameFilter, setCampanhasCorretorNameFilter] = useState<string>('')
  const [campanhasClassificationFilter, setCampanhasClassificationFilter] = useState<string>('')

  // Estados para "Minimetas 2026"
  const [minimetas2026Items, setMinimetas2026Items] = useState<DashCorretorMinimetas2026Entry[]>([])
  const [loadingMinimetas2026, setLoadingMinimetas2026] = useState<boolean>(false)
  const [currentPageMinimetas2026, setCurrentPageMinimetas2026] = useState(1)
  const [itemsPerPageMinimetas2026] = useState(20)

  // Filtros por intervalo (quantidade inicial e final) na sub-aba "Minimetas 2026"
  const [minQuantidadeMinimetas2026, setMinQuantidadeMinimetas2026] = useState<string>('')
  const [maxQuantidadeMinimetas2026, setMaxQuantidadeMinimetas2026] = useState<string>('')

  // Filtros por intervalo (quantidade inicial e final) na sub-aba "Rei dos Imediatos"
  const [minQuantidadeReiImediatos, setMinQuantidadeReiImediatos] = useState<string>('')
  const [maxQuantidadeReiImediatos, setMaxQuantidadeReiImediatos] = useState<string>('')

  // Estados para "Decole com Influenciadores"
  const [decoleInfluenciadoresMode, setDecoleInfluenciadoresMode] = useState<'vendas' | 'producao'>('vendas')
  const [decoleInfluenciadoresVendasItems, setDecoleInfluenciadoresVendasItems] = useState<DashCorretorDecoleInfluenciadoresEntry[]>([])
  const [decoleInfluenciadoresProducaoItems, setDecoleInfluenciadoresProducaoItems] = useState<DashCorretorDecoleInfluenciadoresEntry[]>([])
  const [loadingDecoleInfluenciadoresVendas, setLoadingDecoleInfluenciadoresVendas] = useState<boolean>(false)
  const [loadingDecoleInfluenciadoresProducao, setLoadingDecoleInfluenciadoresProducao] = useState<boolean>(false)
  const [currentPageDecoleVendas, setCurrentPageDecoleVendas] = useState(1)
  const [currentPageDecoleProducao, setCurrentPageDecoleProducao] = useState(1)
  const [itemsPerPageDecole] = useState(20)

  // Buscar título e descrição do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('portais_dashboard_corretor')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Buscar histórico de acessos
  const loadHistoricoAcessos = useCallback(async () => {
    if (activeTab !== 'historico') return

    setLoadingHistorico(true)
    try {
      // Buscar dados em paralelo
      const [historico, total, hoje] = await Promise.all([
        dashcorretorService.getHistoricoAcessos({
          corretorCodeQuery: historicoCorretorCodeFilter || undefined,
          corretorQuery: corretorQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined
        }),
        dashcorretorService.getTotalAcessos(),
        dashcorretorService.getAcessosHoje()
      ])

      setHistoricoAcessos(historico)
      setTotalHistorico(total)
      setAcessosHoje(hoje)
    } catch (error) {
      console.error('Erro ao carregar histórico de acessos:', error)
      // Em caso de erro, definir valores padrão
      setHistoricoAcessos([])
      setTotalHistorico(0)
      setAcessosHoje(0)
    } finally {
      setLoadingHistorico(false)
    }
  }, [activeTab, historicoCorretorCodeFilter, corretorQuery, statusFilter, dateFrom, dateTo])

  useEffect(() => {
    loadHistoricoAcessos()
  }, [loadHistoricoAcessos])

  // Função para alternar ordenação
  const toggleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }, [sortBy, sortOrder])

  // Aplicar ordenação e filtros
  const filtrados = useMemo(() => {
    let resultado = [...historicoAcessos]

    // Aplicar ordenação
    resultado.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortBy) {
        case 'corretor':
          aValue = a.corretor || ''
          bValue = b.corretor || ''
          break
        case 'id_scgp':
          aValue = a.id_scgp != null ? Number(a.id_scgp) : Number.POSITIVE_INFINITY
          bValue = b.id_scgp != null ? Number(b.id_scgp) : Number.POSITIVE_INFINITY
          break
        case 'access_timestamp':
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
          break
        case 'route':
          aValue = a.route || ''
          bValue = b.route || ''
          break
        case 'status':
          aValue = a.status || ''
          bValue = b.status || ''
          break
        case 'error_message':
          aValue = a.error_message || ''
          bValue = b.error_message || ''
          break
        case 'user_agent':
          aValue = a.user_agent || ''
          bValue = b.user_agent || ''
          break
        default:
          aValue = a.access_timestamp || ''
          bValue = b.access_timestamp || ''
      }

      // Comparação
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return resultado
  }, [historicoAcessos, sortBy, sortOrder])

  // Calcular dados da paginação
  const totalPages = Math.ceil(filtrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const filtradosPaginados = filtrados.slice(startIndex, endIndex)

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [historicoCorretorCodeFilter, corretorQuery, statusFilter, dateFrom, dateTo])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  const truncateCorretorName = (name: string, maxChars = 60) => {
    if (!name) return ''
    return name.length > maxChars ? `${name.slice(0, maxChars)}...` : name
  }

  // Carregar corretores (tabela brokers) quando abrir a aba
  const loadBrokers = useCallback(async () => {
    setLoadingBrokers(true)
    setBrokers([]) // Limpar dados anteriores
    try {
      console.log('Carregando corretores...')
      const data = await dashcorretorService.listBrokers()
      console.log(`Corretores carregados: ${data.length} registros`)
      setBrokers(data)
      if (data.length === 0) {
        console.warn('Nenhum corretor encontrado na tabela brokers')
      }
    } catch (error) {
      console.error('Erro ao carregar corretores:', error)
      if (error instanceof Error) {
        console.error('Mensagem de erro:', error.message)
        console.error('Stack:', error.stack)
      }
      // Manter array vazio em caso de erro
      setBrokers([])
    } finally {
      setLoadingBrokers(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'corretores') {
      setCurrentPageBrokers(1) // Resetar página ao abrir a aba
    }
    if ((activeTab === 'corretores' || activeTab === 'campanhas') && !loadingBrokers && brokers.length === 0) {
      loadBrokers()
    }
  }, [activeTab, loadBrokers, loadingBrokers, brokers.length])

  // Carregar parâmetros do dashboard corretor
  const loadParameters = useCallback(async () => {
    if (activeTab !== 'parametros') return

    setLoadingParameters(true)
    try {
      const data = await dashcorretorService.listParameters()
      setParameters(data)
    } catch (error) {
      console.error('Erro ao carregar parâmetros do dashboard corretor:', error)
      setParameters([])
    } finally {
      setLoadingParameters(false)
    }
  }, [activeTab])

  useEffect(() => {
    loadParameters()
  }, [loadParameters])

  // Carregar dados de Campeão do Mês
  const loadCampeaoMes = useCallback(async () => {
    if (activeTab !== 'campanhas' || activeSubTab !== 'campeaoMes') return

    setLoadingCampeaoMes(true)
    try {
      const data = await dashcorretorService.listCampeaoMes(canViewValues)
      setCampeaoMesItems(data)
    } catch (error) {
      console.error('Erro ao carregar dados de Campeão do Mês:', error)
      setCampeaoMesItems([])
    } finally {
      setLoadingCampeaoMes(false)
    }
  }, [activeTab, activeSubTab, canViewValues])

  useEffect(() => {
    loadCampeaoMes()
  }, [loadCampeaoMes])

  // Carregar dados de "Rei dos Imediatos"
  const loadReiImediatos = useCallback(async () => {
    if (activeTab !== 'campanhas' || activeSubTab !== 'reiImediatos') return

    setLoadingReiImediatos(true)
    try {
      const data = await dashcorretorService.listReiImediatos(canViewValues)
      setReiImediatosItems(data)
    } catch (error) {
      console.error('Erro ao carregar dados de Rei dos Imediatos:', error)
      setReiImediatosItems([])
    } finally {
      setLoadingReiImediatos(false)
    }
  }, [activeTab, activeSubTab, canViewValues])

  useEffect(() => {
    loadReiImediatos()
  }, [loadReiImediatos])

  // Carregar dados de "Minimetas 2026"
  const loadMinimetas2026 = useCallback(async () => {
    if (activeTab !== 'campanhas' || activeSubTab !== 'minimetas2026') return

    setLoadingMinimetas2026(true)
    try {
      const data = await dashcorretorService.listMinimetas2026(canViewValues)
      setMinimetas2026Items(data)
    } catch (error) {
      console.error('Erro ao carregar dados de Minimetas 2026:', error)
      setMinimetas2026Items([])
    } finally {
      setLoadingMinimetas2026(false)
    }
  }, [activeTab, activeSubTab, canViewValues])

  useEffect(() => {
    loadMinimetas2026()
  }, [loadMinimetas2026])

  const loadDecoleInfluenciadoresVendas = useCallback(async () => {
    if (activeTab !== 'campanhas' || activeSubTab !== 'decoleInfluenciadores' || decoleInfluenciadoresMode !== 'vendas') return

    setLoadingDecoleInfluenciadoresVendas(true)
    try {
      const data = await dashcorretorService.listDecoleInfluenciadoresVendas(canViewValues)
      setDecoleInfluenciadoresVendasItems(data)
    } catch (error) {
      console.error('Erro ao carregar dados de Decole com Influenciadores (Vendas):', error)
      setDecoleInfluenciadoresVendasItems([])
    } finally {
      setLoadingDecoleInfluenciadoresVendas(false)
    }
  }, [activeTab, activeSubTab, decoleInfluenciadoresMode, canViewValues])

  useEffect(() => {
    loadDecoleInfluenciadoresVendas()
  }, [loadDecoleInfluenciadoresVendas])

  const loadDecoleInfluenciadoresProducao = useCallback(async () => {
    if (activeTab !== 'campanhas' || activeSubTab !== 'decoleInfluenciadores' || decoleInfluenciadoresMode !== 'producao') return

    setLoadingDecoleInfluenciadoresProducao(true)
    try {
      const data = await dashcorretorService.listDecoleInfluenciadoresProducao(canViewValues)
      setDecoleInfluenciadoresProducaoItems(data)
    } catch (error) {
      console.error('Erro ao carregar dados de Decole com Influenciadores (Produção):', error)
      setDecoleInfluenciadoresProducaoItems([])
    } finally {
      setLoadingDecoleInfluenciadoresProducao(false)
    }
  }, [activeTab, activeSubTab, decoleInfluenciadoresMode, canViewValues])

  useEffect(() => {
    loadDecoleInfluenciadoresProducao()
  }, [loadDecoleInfluenciadoresProducao])

  const sortedReiImediatosItems = useMemo(() => {
    const items = [...reiImediatosItems]
    return canViewValues
      ? items.sort((a, b) => b.quantidade - a.quantidade)
      : items.sort((a, b) => (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR'))
  }, [reiImediatosItems, canViewValues])

  const sortedMinimetas2026Items = useMemo(() => {
    const items = [...minimetas2026Items]
    return canViewValues
      ? items.sort((a, b) => b.quantidade - a.quantidade)
      : items.sort((a, b) => (a.broker_name || '').localeCompare(b.broker_name || '', 'pt-BR'))
  }, [minimetas2026Items, canViewValues])

  const filteredReiImediatosItems = useMemo(() => {
    const parseQuantidade = (input: string): number | null => {
      const trimmed = input.trim()
      if (!trimmed) return null

      // Aceita "1.234,56" (pt-BR) e "1234.56" (en-US).
      // - Se tiver vírgula, considera vírgula como decimal e remove pontos de milhar.
      // - Caso contrário, considera ponto como decimal e remove vírgulas de milhar.
      const normalized = trimmed.includes(',')
        ? trimmed.replace(/\./g, '').replace(',', '.')
        : trimmed.replace(/,/g, '')
      const n = Number(normalized)
      return Number.isFinite(n) ? n : null
    }

    const minQ = parseQuantidade(minQuantidadeReiImediatos)
    const maxQ = parseQuantidade(maxQuantidadeReiImediatos)

    return sortedReiImediatosItems.filter((item) => {
      if (
        !campaignRowMatchesFilters(
          item,
          campanhasCorretorCodeFilter,
          campanhasCorretorNameFilter,
          campanhasClassificationFilter
        )
      ) {
        return false
      }

      const matchMin = minQ == null || item.quantidade >= minQ
      const matchMax = maxQ == null || item.quantidade <= maxQ
      return matchMin && matchMax
    })
  }, [
    sortedReiImediatosItems,
    campanhasCorretorCodeFilter,
    campanhasCorretorNameFilter,
    campanhasClassificationFilter,
    minQuantidadeReiImediatos,
    maxQuantidadeReiImediatos
  ])

  const totalPagesReiImediatos = Math.ceil(filteredReiImediatosItems.length / itemsPerPageReiImediatos)
  const startIndexReiImediatos = (currentPageReiImediatos - 1) * itemsPerPageReiImediatos
  const endIndexReiImediatos = startIndexReiImediatos + itemsPerPageReiImediatos
  const pagedReiImediatosItems = filteredReiImediatosItems.slice(startIndexReiImediatos, endIndexReiImediatos)

  const handlePageChangeReiImediatos = useCallback((page: number) => {
    setCurrentPageReiImediatos(page)
  }, [])

  const handlePreviousPageReiImediatos = useCallback(() => {
    if (currentPageReiImediatos > 1) {
      setCurrentPageReiImediatos(currentPageReiImediatos - 1)
    }
  }, [currentPageReiImediatos])

  const handleNextPageReiImediatos = useCallback(() => {
    if (currentPageReiImediatos < totalPagesReiImediatos) {
      setCurrentPageReiImediatos(currentPageReiImediatos + 1)
    }
  }, [currentPageReiImediatos, totalPagesReiImediatos])

  const filteredMinimetas2026Items = useMemo(() => {
    const parseQuantidade = (input: string): number | null => {
      const trimmed = input.trim()
      if (!trimmed) return null

      // Aceita "1.234,56" (pt-BR) e "1234.56" (en-US).
      // - Se tiver vírgula, considera vírgula como decimal e remove pontos de milhar.
      // - Caso contrário, considera ponto como decimal e remove vírgulas de milhar.
      const normalized = trimmed.includes(',')
        ? trimmed.replace(/\./g, '').replace(',', '.')
        : trimmed.replace(/,/g, '')
      const n = Number(normalized)
      return Number.isFinite(n) ? n : null
    }

    const minQ = parseQuantidade(minQuantidadeMinimetas2026)
    const maxQ = parseQuantidade(maxQuantidadeMinimetas2026)

    return sortedMinimetas2026Items.filter((item) => {
      if (!campaignRowMatchesFilters(item, campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter)) {
        return false
      }

      const matchMin = minQ == null || item.quantidade >= minQ
      const matchMax = maxQ == null || item.quantidade <= maxQ
      return matchMin && matchMax
    })
  }, [
    sortedMinimetas2026Items,
    campanhasCorretorCodeFilter,
    campanhasCorretorNameFilter,
    campanhasClassificationFilter,
    minQuantidadeMinimetas2026,
    maxQuantidadeMinimetas2026
  ])

  const totalPagesMinimetas2026 = Math.ceil(filteredMinimetas2026Items.length / itemsPerPageMinimetas2026)
  const startIndexMinimetas2026 = (currentPageMinimetas2026 - 1) * itemsPerPageMinimetas2026
  const endIndexMinimetas2026 = startIndexMinimetas2026 + itemsPerPageMinimetas2026
  const pagedMinimetas2026Items = filteredMinimetas2026Items.slice(startIndexMinimetas2026, endIndexMinimetas2026)

  const handlePageChangeMinimetas2026 = useCallback((page: number) => {
    setCurrentPageMinimetas2026(page)
  }, [])

  const handlePreviousPageMinimetas2026 = useCallback(() => {
    if (currentPageMinimetas2026 > 1) {
      setCurrentPageMinimetas2026(currentPageMinimetas2026 - 1)
    }
  }, [currentPageMinimetas2026])

  const handleNextPageMinimetas2026 = useCallback(() => {
    if (currentPageMinimetas2026 < totalPagesMinimetas2026) {
      setCurrentPageMinimetas2026(currentPageMinimetas2026 + 1)
    }
  }, [currentPageMinimetas2026, totalPagesMinimetas2026])

  const sortedCampeaoMesItems = useMemo(() => {
    const filtered = campeaoMesItems.filter((item) =>
      campaignRowMatchesFilters(item, campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter)
    )
    return [...filtered].sort((a, b) => {
      const monthA = getReferenceMonthSortValue(a.reference_month)
      const monthB = getReferenceMonthSortValue(b.reference_month)
      return monthA - monthB
    })
  }, [campeaoMesItems, campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter])

  const rankedDecoleInfluenciadoresVendasItems = useMemo(() => {
    const maybeSorted = canViewValues
      ? [...decoleInfluenciadoresVendasItems].sort((a, b) => {
          if (b.value !== a.value) return b.value - a.value
          return a.id.localeCompare(b.id)
        })
      : decoleInfluenciadoresVendasItems

    const withRank = maybeSorted.map((item, index) => ({
      ...item,
      rank: index + 1,
    }))

    return withRank.filter((item) =>
      campaignRowMatchesFilters(item, campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter)
    )
  }, [
    decoleInfluenciadoresVendasItems,
    campanhasCorretorCodeFilter,
    campanhasCorretorNameFilter,
    campanhasClassificationFilter,
    canViewValues,
  ])

  const rankedDecoleInfluenciadoresProducaoItems = useMemo(() => {
    const maybeSorted = canViewValues
      ? [...decoleInfluenciadoresProducaoItems].sort((a, b) => {
          if (b.value !== a.value) return b.value - a.value
          return a.id.localeCompare(b.id)
        })
      : decoleInfluenciadoresProducaoItems

    const withRank = maybeSorted.map((item, index) => ({
      ...item,
      rank: index + 1,
    }))

    return withRank.filter((item) =>
      campaignRowMatchesFilters(item, campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter)
    )
  }, [
    decoleInfluenciadoresProducaoItems,
    campanhasCorretorCodeFilter,
    campanhasCorretorNameFilter,
    campanhasClassificationFilter,
    canViewValues,
  ])

  const totalPagesDecoleVendas = Math.ceil(rankedDecoleInfluenciadoresVendasItems.length / itemsPerPageDecole)
  const startIndexDecoleVendas = (currentPageDecoleVendas - 1) * itemsPerPageDecole
  const endIndexDecoleVendas = startIndexDecoleVendas + itemsPerPageDecole
  const pagedDecoleInfluenciadoresVendasItems = rankedDecoleInfluenciadoresVendasItems.slice(startIndexDecoleVendas, endIndexDecoleVendas)

  const totalPagesDecoleProducao = Math.ceil(rankedDecoleInfluenciadoresProducaoItems.length / itemsPerPageDecole)
  const startIndexDecoleProducao = (currentPageDecoleProducao - 1) * itemsPerPageDecole
  const endIndexDecoleProducao = startIndexDecoleProducao + itemsPerPageDecole
  const pagedDecoleInfluenciadoresProducaoItems = rankedDecoleInfluenciadoresProducaoItems.slice(startIndexDecoleProducao, endIndexDecoleProducao)

  const handlePageChangeDecoleVendas = useCallback((page: number) => {
    setCurrentPageDecoleVendas(page)
  }, [])

  const handlePreviousPageDecoleVendas = useCallback(() => {
    if (currentPageDecoleVendas > 1) {
      setCurrentPageDecoleVendas(currentPageDecoleVendas - 1)
    }
  }, [currentPageDecoleVendas])

  const handleNextPageDecoleVendas = useCallback(() => {
    if (currentPageDecoleVendas < totalPagesDecoleVendas) {
      setCurrentPageDecoleVendas(currentPageDecoleVendas + 1)
    }
  }, [currentPageDecoleVendas, totalPagesDecoleVendas])

  const handlePageChangeDecoleProducao = useCallback((page: number) => {
    setCurrentPageDecoleProducao(page)
  }, [])

  const handlePreviousPageDecoleProducao = useCallback(() => {
    if (currentPageDecoleProducao > 1) {
      setCurrentPageDecoleProducao(currentPageDecoleProducao - 1)
    }
  }, [currentPageDecoleProducao])

  const handleNextPageDecoleProducao = useCallback(() => {
    if (currentPageDecoleProducao < totalPagesDecoleProducao) {
      setCurrentPageDecoleProducao(currentPageDecoleProducao + 1)
    }
  }, [currentPageDecoleProducao, totalPagesDecoleProducao])

  useEffect(() => {
    setCurrentPageDecoleVendas(1)
  }, [decoleInfluenciadoresVendasItems])

  useEffect(() => {
    setCurrentPageDecoleProducao(1)
  }, [decoleInfluenciadoresProducaoItems])

  useEffect(() => {
    setCurrentPageReiImediatos(1)
  }, [reiImediatosItems])

  useEffect(() => {
    setCurrentPageMinimetas2026(1)
  }, [minimetas2026Items])

  useEffect(() => {
    setCurrentPageDecoleVendas(1)
    setCurrentPageDecoleProducao(1)
    setCurrentPageReiImediatos(1)
    setCurrentPageMinimetas2026(1)
  }, [campanhasCorretorCodeFilter, campanhasCorretorNameFilter, campanhasClassificationFilter])

  useEffect(() => {
    setCurrentPageMinimetas2026(1)
  }, [minQuantidadeMinimetas2026, maxQuantidadeMinimetas2026])

  useEffect(() => {
    setCurrentPageReiImediatos(1)
  }, [minQuantidadeReiImediatos, maxQuantidadeReiImediatos])

  // Se o usuário não tem permissão para ver valores, não faz sentido manter filtros de quantidade ativos.
  useEffect(() => {
    if (!canViewValues) {
      setMinQuantidadeMinimetas2026('')
      setMaxQuantidadeMinimetas2026('')
    }
  }, [canViewValues])

  useEffect(() => {
    if (!canViewValues) {
      setMinQuantidadeReiImediatos('')
      setMaxQuantidadeReiImediatos('')
    }
  }, [canViewValues])

  const classificationsBrokers = useMemo(() => {
    const set = new Set<string>()
    brokers.forEach((b) => {
      if (b.classification?.trim()) set.add(b.classification.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [brokers])

  const ufsBrokers = useMemo(() => {
    const set = new Set<string>()
    brokers.forEach((b) => {
      if (b.state?.trim()) set.add(b.state.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [brokers])

  /** Cidades disponíveis no dropdown: todas se UF vazio; senão só da UF selecionada */
  const citiesBrokersOptions = useMemo(() => {
    const source = corretorUfFilter
      ? brokers.filter((b) => (b.state || '').trim() === corretorUfFilter)
      : brokers
    const set = new Set<string>()
    source.forEach((b) => {
      if (b.city?.trim()) set.add(b.city.trim())
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [brokers, corretorUfFilter])

  useEffect(() => {
    if (corretorCityFilter && !citiesBrokersOptions.includes(corretorCityFilter)) {
      setCorretorCityFilter('')
    }
  }, [corretorUfFilter, citiesBrokersOptions, corretorCityFilter])

  const filteredBrokers = useMemo(() => {
    let result = brokers
    const code = corretorCodeFilter.trim()
    if (code) {
      result = result.filter((b) => String(b.id_scgp ?? '').includes(code))
    }
    const name = corretorNameFilter.trim().toLowerCase()
    if (name) {
      result = result.filter((b) => (b.name || '').toLowerCase().includes(name))
    }
    if (corretorClassificationFilter) {
      result = result.filter((b) => (b.classification || '').trim() === corretorClassificationFilter)
    }
    if (corretorUfFilter) {
      result = result.filter((b) => (b.state || '').trim() === corretorUfFilter)
    }
    if (corretorCityFilter) {
      result = result.filter((b) => (b.city || '').trim() === corretorCityFilter)
    }
    return result
  }, [
    brokers,
    corretorCodeFilter,
    corretorNameFilter,
    corretorClassificationFilter,
    corretorUfFilter,
    corretorCityFilter,
  ])

  // Calcular dados da paginação para corretores
  const totalPagesBrokers = Math.ceil(filteredBrokers.length / itemsPerPageBrokers)
  const startIndexBrokers = (currentPageBrokers - 1) * itemsPerPageBrokers
  const endIndexBrokers = startIndexBrokers + itemsPerPageBrokers
  const filteredBrokersPaginados = filteredBrokers.slice(startIndexBrokers, endIndexBrokers)

  // Handlers de paginação para corretores
  const handlePageChangeBrokers = useCallback((page: number) => {
    setCurrentPageBrokers(page)
  }, [])

  const handlePreviousPageBrokers = useCallback(() => {
    if (currentPageBrokers > 1) {
      setCurrentPageBrokers(currentPageBrokers - 1)
    }
  }, [currentPageBrokers])

  const handleNextPageBrokers = useCallback(() => {
    if (currentPageBrokers < totalPagesBrokers) {
      setCurrentPageBrokers(currentPageBrokers + 1)
    }
  }, [currentPageBrokers, totalPagesBrokers])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPageBrokers(1)
  }, [
    corretorCodeFilter,
    corretorNameFilter,
    corretorClassificationFilter,
    corretorUfFilter,
    corretorCityFilter,
  ])

  const hasCorretoresFiltersActive = useMemo(
    () =>
      !!(
        corretorCodeFilter.trim() ||
        corretorNameFilter.trim() ||
        corretorClassificationFilter ||
        corretorUfFilter ||
        corretorCityFilter
      ),
    [
      corretorCodeFilter,
      corretorNameFilter,
      corretorClassificationFilter,
      corretorUfFilter,
      corretorCityFilter,
    ]
  )
  

  const handleAcessar = async (broker: Broker) => {
    const codigocorretor = broker.id_scgp
    if (codigocorretor == null) {
      alert('Este corretor não possui código SCGP (id_scgp).')
      return
    }
    setAcessandoBrokerId(broker.id)
    try {
      const result = await serverInfoService.getDashcorretorLink(codigocorretor)
      if (result.success && result.data) {
        window.open(result.data, '_blank')
        try {
          await userLogsService.logUserAction({
            action: 'view',
            entityType: 'other',
            entityId: broker.id,
            description: `Acessou dashboard do corretor ${broker.name || broker.id}`,
            metadata: {
              broker_id: broker.id,
              broker_name: broker.name,
              id_scgp: broker.id_scgp,
              link: result.data,
              origem: 'portais/dashboard_corretor',
            },
          })
        } catch (logError) {
          console.warn('Erro ao registrar log de acesso ao dashboard corretor:', logError)
        }
      } else {
        throw new Error(result.error || 'Erro ao gerar link')
      }
    } catch (error) {
      console.error('Erro ao acessar dashboard do corretor:', error)
      alert(error instanceof Error ? error.message : 'Erro ao gerar link. Tente novamente.')
    } finally {
      setAcessandoBrokerId(null)
    }
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName="portais_dashboard_corretor"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
              <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
            </div>

            {/* Navegação das Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('campanhas')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'campanhas'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Campanhas 2026
                </button>
                <button
                  onClick={() => setActiveTab('historico')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'historico'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Histórico de Acessos
                </button>
                <button
                  onClick={() => setActiveTab('corretores')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'corretores'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Corretores
                </button>
                <button
                  onClick={() => setActiveTab('parametros')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'parametros'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Parâmetros
                </button>
              </nav>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'campanhas' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="px-6 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Código do corretor
                      </Text>
                      <Input
                        type="text"
                        placeholder="Filtrar por código..."
                        value={campanhasCorretorCodeFilter}
                        onChange={(e) => setCampanhasCorretorCodeFilter(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Nome do corretor
                      </Text>
                      <Input
                        type="text"
                        placeholder="Filtrar por nome..."
                        value={campanhasCorretorNameFilter}
                        onChange={(e) => setCampanhasCorretorNameFilter(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Classificação
                      </Text>
                      <Select
                        value={campanhasClassificationFilter}
                        onChange={(e) => setCampanhasClassificationFilter(e.target.value)}
                        disabled={loadingBrokers && brokers.length === 0}
                      >
                        <option value="">Todas</option>
                        {classificationsBrokers.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full md:w-auto"
                        type="button"
                        onClick={() => {
                          setCampanhasCorretorCodeFilter('')
                          setCampanhasCorretorNameFilter('')
                          setCampanhasClassificationFilter('')
                        }}
                        disabled={!campanhasCorretorCodeFilter.trim() && !campanhasCorretorNameFilter.trim() && !campanhasClassificationFilter.trim()}
                      >
                        Limpar filtros
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Navegação das Sub-abas */}
                <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <nav className="flex space-x-4 px-6">
                    <button
                      onClick={() => setActiveSubTab('minimetas2026')}
                      className={`py-3 px-3 border-b-2 font-medium text-xs ${
                        activeSubTab === 'minimetas2026'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Minimetas
                    </button>
                    <button
                      onClick={() => setActiveSubTab('campeaoMes')}
                      className={`py-3 px-3 border-b-2 font-medium text-xs ${
                        activeSubTab === 'campeaoMes'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Campeão do Mês
                    </button>
                    <button
                      onClick={() => setActiveSubTab('decoleInfluenciadores')}
                      className={`py-3 px-3 border-b-2 font-medium text-xs ${
                        activeSubTab === 'decoleInfluenciadores'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Decole com Influenciadores
                    </button>
                    <button
                      onClick={() => setActiveSubTab('reiImediatos')}
                      className={`py-3 px-3 border-b-2 font-medium text-xs ${
                        activeSubTab === 'reiImediatos'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Bateu, Levou! Imediatos
                    </button>
                  </nav>
                </div>

                {/* Conteúdo das Sub-abas */}
                <div className="flex-1 px-6 py-6">
                  {activeSubTab === 'minimetas2026' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-y-3 gap-x-6">
                        <div>
                          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                            Minimetas
                          </Heading>
                        </div>
                        {canViewValues && (
                          <div className="w-auto">
                            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 justify-end">
                              <div className="w-full sm:w-48">
                                <div className="flex items-center gap-2">
                                  <Text size="xs" className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Qtd. inicial
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="number"
                                    inputMode="numeric"
                                    step="1"
                                    placeholder="Min"
                                    value={minQuantidadeMinimetas2026}
                                    onChange={(e) => setMinQuantidadeMinimetas2026(e.target.value)}
                                    className="flex-1"
                                  />
                                </div>
                              </div>
                              <div className="w-full sm:w-48">
                                <div className="flex items-center gap-2">
                                  <Text size="xs" className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Qtd. final
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="number"
                                    inputMode="numeric"
                                    step="1"
                                    placeholder="Max"
                                    value={maxQuantidadeMinimetas2026}
                                    onChange={(e) => setMaxQuantidadeMinimetas2026(e.target.value)}
                                    className="flex-1"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  type="button"
                                  aria-label="Limpar filtros de quantidade"
                                  title="Limpar filtros de quantidade"
                                  onClick={() => {
                                    setMinQuantidadeMinimetas2026('')
                                    setMaxQuantidadeMinimetas2026('')
                                  }}
                                  disabled={!minQuantidadeMinimetas2026.trim() && !maxQuantidadeMinimetas2026.trim()}
                                >
                                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <div className="flex-1 overflow-auto">
                          {loadingMinimetas2026 ? (
                            <div className="flex items-center justify-center py-12">
                              <LoadingSpinner />
                            </div>
                          ) : filteredMinimetas2026Items.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                              <Text size="sm" color="muted">
                                Nenhum registro encontrado.
                              </Text>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Código
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Corretor
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      UF
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Classificação
                                    </th>
                                    {canViewValues && (
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Quantidade
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {pagedMinimetas2026Items.map((item) => (
                                    <tr key={item.broker_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.broker_code ?? '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap" title={item.broker_name}>
                                        {truncateCorretorName(item.broker_name)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.state?.trim() || '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.classification ?? '—'}
                                      </td>
                                      {canViewValues && (
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap text-right">
                                          {item.quantidade.toLocaleString('pt-BR')}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {totalPagesMinimetas2026 > 1 && !loadingMinimetas2026 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexMinimetas2026 + 1} a {Math.min(endIndexMinimetas2026, filteredMinimetas2026Items.length)} de {filteredMinimetas2026Items.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageMinimetas2026}
                                  disabled={currentPageMinimetas2026 === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesMinimetas2026) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesMinimetas2026 <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageMinimetas2026 <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageMinimetas2026 >= totalPagesMinimetas2026 - 2) {
                                      pageNumber = totalPagesMinimetas2026 - 4 + i
                                    } else {
                                      pageNumber = currentPageMinimetas2026 - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeMinimetas2026(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageMinimetas2026 === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageMinimetas2026}
                                  disabled={currentPageMinimetas2026 === totalPagesMinimetas2026}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSubTab === 'campeaoMes' && (
                    <div className="space-y-6">
                      <div>
                        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                          Campeão do Mês
                        </Heading>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <div className="flex-1 overflow-auto">
                          {loadingCampeaoMes ? (
                            <div className="flex items-center justify-center py-12">
                              <LoadingSpinner />
                            </div>
                          ) : campeaoMesItems.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                              <Text size="sm" color="muted">
                                Nenhum registro encontrado.
                              </Text>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Mês
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Corretor
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      UF
                                    </th>
                                    {canViewValues && (
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Valor
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {sortedCampeaoMesItems.map((item) => (
                                    <tr key={`${item.reference_month}-${item.campaign_id}-${item.broker_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {formatReferenceMonth(item.reference_month)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200" title={item.broker_name}>
                                        {truncateCorretorName(item.broker_name)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.state?.trim() || '—'}
                                      </td>
                                      {canViewValues && (
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                          {item.value.toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            maximumFractionDigits: 2,
                                          })}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeSubTab === 'decoleInfluenciadores' && (
                    <div className="space-y-6">
                      <div>
                        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                          Decole com Influenciadores
                        </Heading>
                        <Text size="sm" color="muted" className="mt-2">
                          Top 10 vendas e Top 20 produção
                        </Text>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                          <nav className="flex space-x-4 px-6">
                            <button
                              onClick={() => setDecoleInfluenciadoresMode('vendas')}
                              className={`py-3 px-3 border-b-2 font-medium text-xs ${
                                decoleInfluenciadoresMode === 'vendas'
                                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Vendas
                            </button>
                            <button
                              onClick={() => setDecoleInfluenciadoresMode('producao')}
                              className={`py-3 px-3 border-b-2 font-medium text-xs ${
                                decoleInfluenciadoresMode === 'producao'
                                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                              }`}
                            >
                              Produção
                            </button>
                          </nav>
                        </div>
                        <div className="flex-1 overflow-auto">
                          {decoleInfluenciadoresMode === 'vendas' ? (
                            loadingDecoleInfluenciadoresVendas ? (
                              <div className="flex items-center justify-center py-12">
                                <LoadingSpinner />
                              </div>
                            ) : rankedDecoleInfluenciadoresVendasItems.length === 0 ? (
                              <div className="flex items-center justify-center py-12">
                                <Text size="sm" color="muted">
                                  Nenhum registro encontrado.
                                </Text>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                                  <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Ranking
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Código
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Corretor
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        UF
                                      </th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Classificação
                                      </th>
                                      {canViewValues && (
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                          Valor
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {pagedDecoleInfluenciadoresVendasItems.map((item) => (
                                      <tr
                                        key={item.id}
                                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${item.rank <= 10 ? 'bg-emerald-100 dark:bg-emerald-900/30' : ''}`}
                                      >
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap font-medium">
                                          {item.rank}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                          {item.broker_code ?? '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap" title={item.broker_name}>
                                          {truncateCorretorName(item.broker_name)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                          {item.state?.trim() || '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                          {item.classification ?? '—'}
                                        </td>
                                        {canViewValues && (
                                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap text-right">
                                            {item.value.toLocaleString('pt-BR', {
                                              maximumFractionDigits: 2,
                                            })}
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          ) : loadingDecoleInfluenciadoresProducao ? (
                            <div className="flex items-center justify-center py-12">
                              <LoadingSpinner />
                            </div>
                          ) : rankedDecoleInfluenciadoresProducaoItems.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                              <Text size="sm" color="muted">
                                Nenhum registro encontrado.
                              </Text>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Ranking
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Código
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Corretor
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      UF
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Classificação
                                    </th>
                                    {canViewValues && (
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Valor
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {pagedDecoleInfluenciadoresProducaoItems.map((item) => (
                                    <tr
                                      key={item.id}
                                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${item.rank <= 20 ? 'bg-emerald-100 dark:bg-emerald-900/30' : ''}`}
                                    >
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap font-medium">
                                        {item.rank}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.broker_code ?? '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap" title={item.broker_name}>
                                        {truncateCorretorName(item.broker_name)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.state?.trim() || '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.classification ?? '—'}
                                      </td>
                                      {canViewValues && (
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap text-right">
                                          {item.value.toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            maximumFractionDigits: 2,
                                          })}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {decoleInfluenciadoresMode === 'vendas' && totalPagesDecoleVendas > 1 && !loadingDecoleInfluenciadoresVendas && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexDecoleVendas + 1} a {Math.min(endIndexDecoleVendas, rankedDecoleInfluenciadoresVendasItems.length)} de {rankedDecoleInfluenciadoresVendasItems.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageDecoleVendas}
                                  disabled={currentPageDecoleVendas === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesDecoleVendas) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesDecoleVendas <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageDecoleVendas <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageDecoleVendas >= totalPagesDecoleVendas - 2) {
                                      pageNumber = totalPagesDecoleVendas - 4 + i
                                    } else {
                                      pageNumber = currentPageDecoleVendas - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeDecoleVendas(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageDecoleVendas === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageDecoleVendas}
                                  disabled={currentPageDecoleVendas === totalPagesDecoleVendas}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {decoleInfluenciadoresMode === 'producao' && totalPagesDecoleProducao > 1 && !loadingDecoleInfluenciadoresProducao && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexDecoleProducao + 1} a {Math.min(endIndexDecoleProducao, rankedDecoleInfluenciadoresProducaoItems.length)} de {rankedDecoleInfluenciadoresProducaoItems.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageDecoleProducao}
                                  disabled={currentPageDecoleProducao === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesDecoleProducao) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesDecoleProducao <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageDecoleProducao <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageDecoleProducao >= totalPagesDecoleProducao - 2) {
                                      pageNumber = totalPagesDecoleProducao - 4 + i
                                    } else {
                                      pageNumber = currentPageDecoleProducao - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeDecoleProducao(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageDecoleProducao === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageDecoleProducao}
                                  disabled={currentPageDecoleProducao === totalPagesDecoleProducao}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSubTab === 'reiImediatos' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-y-3 gap-x-6">
                        <div>
                          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                            Bateu, Levou! Imediatos
                          </Heading>
                        </div>
                        {canViewValues && (
                          <div className="w-auto">
                            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 justify-end">
                              <div className="w-full sm:w-48">
                                <div className="flex items-center gap-2">
                                  <Text size="xs" className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Qtd. inicial
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="number"
                                    inputMode="numeric"
                                    step="1"
                                    placeholder="Min"
                                    value={minQuantidadeReiImediatos}
                                    onChange={(e) => setMinQuantidadeReiImediatos(e.target.value)}
                                    className="flex-1"
                                  />
                                </div>
                              </div>
                              <div className="w-full sm:w-48">
                                <div className="flex items-center gap-2">
                                  <Text size="xs" className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    Qtd. final
                                  </Text>
                                  <Input
                                    size="sm"
                                    type="number"
                                    inputMode="numeric"
                                    step="1"
                                    placeholder="Max"
                                    value={maxQuantidadeReiImediatos}
                                    onChange={(e) => setMaxQuantidadeReiImediatos(e.target.value)}
                                    className="flex-1"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  type="button"
                                  aria-label="Limpar filtros de quantidade"
                                  title="Limpar filtros de quantidade"
                                  onClick={() => {
                                    setMinQuantidadeReiImediatos('')
                                    setMaxQuantidadeReiImediatos('')
                                  }}
                                  disabled={!minQuantidadeReiImediatos.trim() && !maxQuantidadeReiImediatos.trim()}
                                >
                                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <div className="flex-1 overflow-auto">
                          {loadingReiImediatos ? (
                            <div className="flex items-center justify-center py-12">
                              <LoadingSpinner />
                            </div>
                          ) : filteredReiImediatosItems.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                              <Text size="sm" color="muted">
                                Nenhum registro encontrado.
                              </Text>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Código
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Corretor
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      UF
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                      Classificação
                                    </th>
                                    {canViewValues && (
                                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                        Quantidade
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {pagedReiImediatosItems.map((item) => (
                                    <tr key={item.broker_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.broker_code ?? '—'}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap"
                                        title={item.broker_name}
                                      >
                                        {truncateCorretorName(item.broker_name)}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.state?.trim() || '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                        {item.classification || '—'}
                                      </td>
                                      {canViewValues && (
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap text-right">
                                          {item.quantidade.toLocaleString('pt-BR')}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {totalPagesReiImediatos > 1 && !loadingReiImediatos && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexReiImediatos + 1} a {Math.min(endIndexReiImediatos, filteredReiImediatosItems.length)} de {filteredReiImediatosItems.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageReiImediatos}
                                  disabled={currentPageReiImediatos === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesReiImediatos) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesReiImediatos <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageReiImediatos <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageReiImediatos >= totalPagesReiImediatos - 2) {
                                      pageNumber = totalPagesReiImediatos - 4 + i
                                    } else {
                                      pageNumber = currentPageReiImediatos - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeReiImediatos(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageReiImediatos === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageReiImediatos}
                                  disabled={currentPageReiImediatos === totalPagesReiImediatos}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'historico' && (
              <>
            {/* KPIs */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{totalHistorico}</Text>
                    <Text size="sm" className="text-white">Acessos - Histórico</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{acessosHoje}</Text>
                    <Text size="sm" className="text-white">Acessos Hoje</Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Código do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Código SCGP..."
                      value={historicoCorretorCodeFilter}
                      onChange={(e) => setHistoricoCorretorCodeFilter(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Corretor</Text>
                    <Input
                      type="text"
                      placeholder="Digite o nome do corretor..."
                      value={corretorQuery}
                      onChange={(e) => setCorretorQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                    <select
                      className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'all' | 'success' | 'failed')}
                    >
                      <option value="all">Todos</option>
                      <option value="success">Sucesso</option>
                      <option value="failed">Falha</option>
                    </select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                    <Input
                      type="date"
                      value={dateFrom ? dateFrom.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        // Adicionar hora 00:00:00 para início do dia
                        setDateFrom(dateValue ? `${dateValue}T00:00:00` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                    <Input
                      type="date"
                      value={dateTo ? dateTo.split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value
                        // Adicionar hora 23:59:59 para fim do dia
                        setDateTo(dateValue ? `${dateValue}T23:59:59` : '')
                      }}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full md:w-auto"
                      onClick={() => {
                        setHistoricoCorretorCodeFilter('')
                        setCorretorQuery('')
                        setStatusFilter('all')
                        setDateFrom('')
                        setDateTo('')
                      }}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">Histórico de Acessos</Heading>
                  {!loadingHistorico && filtrados.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollTableBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollTableBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-auto">
                  {loadingHistorico ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div ref={tableScrollRef} className="overflow-x-auto" onWheel={handleTableWheel}>
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader
                              sortKey="id_scgp"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Código
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="corretor" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Corretor
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="access_timestamp" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Data/Hora Acesso
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="route" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Rota
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="status" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Status
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="error_message" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              Mensagem de Erro
                            </SortableTableHeader>
                            <SortableTableHeader 
                              sortKey="user_agent" 
                              currentSort={sortBy} 
                              sortOrder={sortOrder} 
                              onSort={toggleSort}
                              className="px-4 py-3 whitespace-nowrap"
                            >
                              User Agent
                            </SortableTableHeader>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filtradosPaginados.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">Nenhum dado disponível.</Text>
                              </td>
                            </tr>
                          ) : (
                            filtradosPaginados.map((r, index) => (
                              <tr key={`${r.id}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left whitespace-nowrap">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono">
                                    {r.id_scgp != null ? String(r.id_scgp) : '—'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <div className="flex flex-col">
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">{r.corretor || '-'}</Text>
                                    {r.cnpj && (
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">{r.cnpj}</Text>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(r.access_timestamp)}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all max-w-xs">{r.route || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    r.status === 'success' 
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {r.status === 'success' ? 'Sucesso' : 'Falha'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.error_message || ''}>
                                    {r.error_message ? (r.error_message.length > 50 ? `${r.error_message.substring(0, 50)}...` : r.error_message) : '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400 break-all max-w-xs" title={r.user_agent || ''}>
                                    {r.user_agent ? (r.user_agent.length > 50 ? `${r.user_agent.substring(0, 50)}...` : r.user_agent) : '-'}
                                  </Text>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {totalPages > 1 && !loadingHistorico && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, filtrados.length)} de {filtrados.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number
                            if (totalPages <= 5) {
                              pageNumber = i + 1
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i
                            } else {
                              pageNumber = currentPage - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {activeTab === 'corretores' && (
              <>
            {/* Filtros */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Código do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Código SCGP..."
                      value={corretorCodeFilter}
                      onChange={(e) => setCorretorCodeFilter(e.target.value)}
                      className="w-full"
                      disabled={loadingBrokers && brokers.length === 0}
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Nome do corretor
                    </Text>
                    <Input
                      type="text"
                      placeholder="Nome..."
                      value={corretorNameFilter}
                      onChange={(e) => setCorretorNameFilter(e.target.value)}
                      className="w-full"
                      disabled={loadingBrokers && brokers.length === 0}
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Classificação</Text>
                    <Select
                      value={corretorClassificationFilter}
                      onChange={(e) => setCorretorClassificationFilter(e.target.value)}
                      disabled={loadingBrokers && brokers.length === 0}
                    >
                      <option value="">Todas</option>
                      {classificationsBrokers.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">UF</Text>
                    <Select
                      value={corretorUfFilter}
                      onChange={(e) => setCorretorUfFilter(e.target.value)}
                      disabled={loadingBrokers && brokers.length === 0}
                    >
                      <option value="">Todas</option>
                      {ufsBrokers.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Cidade</Text>
                    <Select
                      value={corretorCityFilter}
                      onChange={(e) => setCorretorCityFilter(e.target.value)}
                      disabled={loadingBrokers && brokers.length === 0}
                    >
                      <option value="">Todas</option>
                      {citiesBrokersOptions.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCorretorCodeFilter('')
                      setCorretorNameFilter('')
                      setCorretorClassificationFilter('')
                      setCorretorUfFilter('')
                      setCorretorCityFilter('')
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Corretores
                    {!loadingBrokers && brokers.length > 0 && (
                      <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                        ({brokers.length} {brokers.length === 1 ? 'corretor' : 'corretores'})
                      </span>
                    )}
                  </Heading>
                </div>
                <div className="flex-1 overflow-auto">
                  {loadingBrokers ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Código SCGP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Classificação</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Estado/Cidade</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredBrokers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">
                                  {hasCorretoresFiltersActive
                                    ? `Nenhum corretor encontrado com os filtros aplicados.`
                                    : brokers.length === 0
                                      ? 'Nenhum corretor encontrado na tabela brokers.'
                                      : 'Nenhum dado disponível.'}
                                </Text>
                              </td>
                            </tr>
                          ) : (
                            filteredBrokersPaginados.map((b) => (
                              <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">{b.name || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400 font-mono">{b.id_scgp ?? '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">{b.classification || '-'}</Text>
                                </td>
                                <td className="px-4 py-4 text-left">
                                  <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                    {[b.state, b.city].filter(Boolean).join(' / ') || '-'}
                                  </Text>
                                </td>
                                <td className="px-4 py-4 text-right">
                                  {b.id_scgp != null ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleAcessar(b)}
                                      disabled={acessandoBrokerId === b.id}
                                    >
                                      {acessandoBrokerId === b.id ? (
                                        <span className="inline-flex items-center gap-1">
                                          <LoadingSpinner size="sm" />
                                          Gerando...
                                        </span>
                                      ) : (
                                        'Acessar'
                                      )}
                                    </Button>
                                  ) : (
                                    <Text size="xs" color="muted">Sem código SCGP</Text>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {totalPagesBrokers > 1 && !loadingBrokers && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndexBrokers + 1} a {Math.min(endIndexBrokers, filteredBrokers.length)} de {filteredBrokers.length} registros
                        </Text>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPageBrokers}
                          disabled={currentPageBrokers === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPagesBrokers) }, (_, i) => {
                            let pageNumber: number
                            if (totalPagesBrokers <= 5) {
                              pageNumber = i + 1
                            } else if (currentPageBrokers <= 3) {
                              pageNumber = i + 1
                            } else if (currentPageBrokers >= totalPagesBrokers - 2) {
                              pageNumber = totalPagesBrokers - 4 + i
                            } else {
                              pageNumber = currentPageBrokers - 2 + i
                            }
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChangeBrokers(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPageBrokers === pageNumber
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            )
                          })}
                        </div>
                        <button
                          onClick={handleNextPageBrokers}
                          disabled={currentPageBrokers === totalPagesBrokers}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {activeTab === 'parametros' && (
              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="py-6 px-4 sm:px-6 lg:px-8">
                  <div className="mb-6">
                    <Text size="sm" color="muted">
                      Configurações do Dashboard Corretor. Edite o valor conforme necessário.
                    </Text>
                  </div>

                  {loadingParameters ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                            <th className="px-4 py-2" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {parameters.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum parâmetro cadastrado.
                              </td>
                            </tr>
                          ) : (
                            parameters.map((param) => (
                              <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td
                                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md truncate"
                                  title={param.description ?? undefined}
                                >
                                  {param.description ?? '—'}
                                </td>
                                <td
                                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                                  title={String(param.value).length > 20 ? String(param.value) : undefined}
                                >
                                  {String(param.value).length > 20
                                    ? `${String(param.value).slice(0, 20)}…`
                                    : param.value}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <PermissionGuard moduleName="portais_dashboard_corretor" permissionType="update" fallback={null}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setEditingParameter(param)
                                        setParameterEditValue(param.value)
                                        const hasHtml = typeof param.value === 'string' && param.value.includes('<')
                                        setParameterEditMode(hasHtml ? 'html' : 'simple')
                                        setIsParameterModalOpen(true)
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  </PermissionGuard>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal de Edição de Parâmetro */}
            {isParameterModalOpen && editingParameter && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                  <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={() => {
                      setIsParameterModalOpen(false)
                      setEditingParameter(null)
                      setParameterEditMode('simple')
                    }}
                  />
                  <div
                    className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 pt-5 pb-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Editar parâmetro
                        </Heading>
                        <button
                          type="button"
                          onClick={() => {
                            setIsParameterModalOpen(false)
                            setEditingParameter(null)
                            setParameterEditMode('simple')
                          }}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                          <span className="sr-only">Fechar</span>
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                        {editingParameter.key}
                      </Text>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 block">
                              Valor *
                            </Text>
                            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 overflow-hidden text-xs">
                              <button
                                type="button"
                                className={`px-2 py-1 ${parameterEditMode === 'simple' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('simple')}
                              >
                                Valor simples
                              </button>
                              <button
                                type="button"
                                className={`px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${parameterEditMode === 'html' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                                onClick={() => setParameterEditMode('html')}
                              >
                                Descritivo HTML
                              </button>
                            </div>
                          </div>
                          {parameterEditMode === 'simple' ? (
                            <Input
                              type="text"
                              value={parameterEditValue}
                              onChange={(e) => setParameterEditValue(e.target.value)}
                              className="w-full"
                            />
                          ) : (
                            <div className="w-full text-sm text-gray-600 dark:text-gray-400">
                              <Editor
                                value={parameterEditValue}
                                onChange={(value) => setParameterEditValue(value)}
                                placeholder="Digite o conteúdo em HTML (parágrafos, listas, etc.)"
                                stickyToolbar
                              />
                            </div>
                          )}
                          <Text size="xs" className="mt-1 text-gray-500 dark:text-gray-400">
                            Use \"Valor simples\" para textos ou números diretos, ou \"Descritivo HTML\" para conteúdo formatado com marcadores HTML.
                          </Text>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Descrição
                          </Text>
                          <div className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                            {editingParameter.description ?? '—'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsParameterModalOpen(false)
                            setEditingParameter(null)
                          }}
                        >
                          Cancelar
                        </Button>
                        <PermissionGuard moduleName="portais_dashboard_corretor" permissionType="update" fallback={null}>
                          <Button
                            variant="solid"
                            disabled={savingParameter || !parameterEditValue.trim()}
                            onClick={async () => {
                              if (!editingParameter?.id || !parameterEditValue.trim()) return
                              setSavingParameter(true)
                              const oldValue = editingParameter.value
                              const newValue = parameterEditValue.trim()
                              try {
                                await dashcorretorService.updateParameter(editingParameter.id, {
                                  value: newValue,
                                })
                                await userLogsService.logUpdate(
                                  'parameter',
                                  editingParameter.id,
                                  `Alterou parâmetro no Dashboard Corretor: ${editingParameter.key}`,
                                  { value: oldValue },
                                  { value: newValue },
                                  { parameter_key: editingParameter.key, context: 'dashboard_corretor' }
                                )
                                await loadParameters()
                                setIsParameterModalOpen(false)
                                setEditingParameter(null)
                              } catch (err: any) {
                                console.error('Erro ao salvar parâmetro do dashboard corretor:', err)
                                alert(err?.message || 'Erro ao salvar parâmetro. Tente novamente.')
                              } finally {
                                setSavingParameter(false)
                              }
                            }}
                          >
                            {savingParameter ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </PermissionGuard>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default DashboardCorretor
